/* ==========================================================================
   Media Gallery
   Main image + responsive thumbnails. Reuses global Swiper and the
   zoom-action (PhotoSwipe) custom element for lightbox.
   ========================================================================== */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 1023.98px)';
  var VERTICAL_THUMBS_QUERY = '(min-width: 1024px)';

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function usesVerticalThumbs() {
    return window.matchMedia(VERTICAL_THUMBS_QUERY).matches;
  }

  function debounce(fn, wait) {
    var timer;
    var debounced = function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
    debounced.cancel = function () { clearTimeout(timer); };
    return debounced;
  }

  var MediaGallery = function () {};

  if (typeof HTMLElement === 'function') {
    MediaGallery = class extends HTMLElement {
      constructor() {
        super();
        this._nativeVideos = new Map();
        this._videoPreferences = new Map();
        this._mainSwiper = null;
        this._thumbSwiper = null;
        this._lastIsMobile = null;
        this._lastVerticalThumbs = null;
        this._ready = false;
        this._updatingGroup = false;
        this._currentGroupKey = null;
        this._thumbSlides = null;
        this._swiperReadyTimer = null;
        this._idleInitHandle = null;
        this._idleInitTimer = null;
        this._initRequested = false;
        this._interactionInitFrames = [];
        this._interactionInitTimer = null;
        this._pendingThumbIndex = -1;
        this._lastGalleryWidth = 0;
        // Debounce flag for trackpad wheel events: prevents a single two-finger
        // swipe from firing multiple slideTo calls in rapid succession.
        this._thumbWheelLock = false;
        // Collapse a burst of rapid variant-swatch clicks into a single rebuild
        // so only the final selection renders. Without this, each `change`
        // rebuilds the gallery in turn and intermediate variants flash by.
        this._debouncedSwitchGroup = debounce(function (key) {
          this.switchGroup(key);
        }.bind(this), 90);
      }

      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this._connectionAbort = new AbortController();
        this._initRequested = false;
        this.sectionId = this.dataset.sectionId;
        this.mainEl = this.querySelector('.mg__main.swiper');
        this.thumbEl = this.querySelector('.mg__thumbs.swiper');
        this._prepareNearbyMedia(0);
        this._currentGroupKey = this.dataset.currentGroupKey || null;
        this._lastGalleryWidth = this.getBoundingClientRect().width;
        this.templates = null;
        this._boundResize = debounce(this._onResize.bind(this), 200);
        this._boundEarlyInit = this._handleEarlyInteraction.bind(this);
        window.addEventListener('resize', this._boundResize);
        this.addEventListener('pointerdown', this._boundEarlyInit, { once: true, passive: true });
        this.addEventListener('focusin', this._boundEarlyInit, { once: true });
        // Listen before idle initialization, without parsing the media payload yet.
        this.bindVariantSwitcher(false);
        this._scheduleInit();
      }

      disconnectedCallback() {
        this._connected = false;
        this._ready = false;
        this._connectionAbort?.abort();
        this._variantPicker = null;
        this._boundResize?.cancel();
        this._debouncedSwitchGroup.cancel();
        this._destroyVideos();
        window.removeEventListener('resize', this._boundResize);
        if (this._swiperReadyTimer) {
          clearTimeout(this._swiperReadyTimer);
          this._swiperReadyTimer = null;
        }
        if (this._idleInitHandle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this._idleInitHandle);
        }
        if (this._idleInitTimer) {
          clearTimeout(this._idleInitTimer);
        }
        this._interactionInitFrames.forEach(function (frame) {
          window.cancelAnimationFrame(frame);
        });
        this._interactionInitFrames = [];
        if (this._interactionInitTimer) {
          clearTimeout(this._interactionInitTimer);
          this._interactionInitTimer = null;
        }
        this.removeEventListener('pointerdown', this._boundEarlyInit);
        this.removeEventListener('focusin', this._boundEarlyInit);
        this._destroySwipers();
      }

      _scheduleInit() {
        var self = this;
        var schedule = function () {
          if (!self.isConnected || self._initRequested) return;
          if ('requestIdleCallback' in window) {
            self._idleInitHandle = window.requestIdleCallback(function () {
              self._idleInitHandle = null;
              self._initializeNow();
            });
            return;
          }
          // Safari has no requestIdleCallback. Keep a bounded fallback, but
          // leave the initial rendering window free for the promoted media.
          self._idleInitTimer = setTimeout(function () {
            self._idleInitTimer = null;
            self._initializeNow();
          }, 2500);
        };

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', schedule, { once: true, signal: this._connectionAbort.signal });
        } else {
          schedule();
        }
      }

      _handleEarlyInteraction(event) {
        if (this._initRequested || this._interactionInitFrames.length || this._interactionInitTimer) return;

        var target = event && event.target;
        var thumb = target && target.closest ? target.closest('.mg__thumb') : null;
        if (thumb) this._pendingThumbIndex = this._getThumbSlides().indexOf(thumb);

        var self = this;
        if (!('requestAnimationFrame' in window)) {
          this._interactionInitTimer = setTimeout(function () {
            self._interactionInitTimer = null;
            self._initializeNow();
          }, 0);
          return;
        }

        var firstFrame = window.requestAnimationFrame(function () {
          var secondFrame = window.requestAnimationFrame(function () {
            self._interactionInitFrames = [];
            self._initializeNow();
          });
          self._interactionInitFrames.push(secondFrame);
        });
        this._interactionInitFrames.push(firstFrame);
      }

      _initializeNow() {
        if (this._initRequested) return;
        this._initRequested = true;
        if (this._idleInitHandle && 'cancelIdleCallback' in window) {
          window.cancelIdleCallback(this._idleInitHandle);
          this._idleInitHandle = null;
        }
        if (this._idleInitTimer) {
          clearTimeout(this._idleInitTimer);
          this._idleInitTimer = null;
        }
        this.removeEventListener('pointerdown', this._boundEarlyInit);
        this.removeEventListener('focusin', this._boundEarlyInit);
        this._whenSwiperReady(this.init.bind(this));
      }

      _readTemplates() {
        var el = document.querySelector('script[data-mg-templates="' + this.sectionId + '"]');
        if (!el) return null;
        try {
          return JSON.parse(el.textContent || '{}');
        } catch (e) {
          return null;
        }
      }

      _getTemplates() {
        if (this.templates) return this.templates;
        this.templates = this._readTemplates();
        return this.templates;
      }

      _whenSwiperReady(cb) {
        if (typeof Swiper !== 'undefined') {
          cb();
          return;
        }
        var self = this;
        var run = function () {
          if (!self.isConnected || self._ready || typeof Swiper === 'undefined') return;
          cb();
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', run, { once: true, signal: this._connectionAbort.signal });
        }
        // One bounded fallback covers delayed third-party/global asset execution
        // without keeping a polling interval alive on every product page.
        this._swiperReadyTimer = setTimeout(function () {
          self._swiperReadyTimer = null;
          run();
        }, 2000);
      }

      init() {
        this.initSwipers();
        if (this._pendingThumbIndex >= 0) {
          if (this._thumbSwiper) this._thumbSwiper.slideTo(this._pendingThumbIndex, 0);
          if (this._mainSwiper) this._mainSwiper.slideTo(this._pendingThumbIndex, 0);
          this._pendingThumbIndex = -1;
        }
        this.bindVariantSwitcher();
        this._bindThumbPeek();
        this._ready = true;
      }

      showMediaFromLightbox(imageLink) {
        if (!this.isConnected) return;
        var mainWrapper = this.mainEl && this.mainEl.querySelector('.swiper-wrapper');
        var slide = imageLink && imageLink.closest ? imageLink.closest('.swiper-slide') : null;
        if (!mainWrapper || !slide || slide.parentElement !== mainWrapper) return;

        // PhotoSwipe counts images only; the main gallery can also contain videos.
        var index = Array.prototype.indexOf.call(mainWrapper.children, slide);
        if (index < 0) return;
        if (this._mainSwiper && !this._mainSwiper.destroyed) {
          this._mainSwiper.slideTo(index, 0);
          if (this._thumbSwiper && !this._thumbSwiper.destroyed) this._thumbSwiper.slideTo(index, 0);
        } else {
          this._pendingThumbIndex = index;
          this._initializeNow();
        }
        if (this.contains(document.activeElement) && document.activeElement !== imageLink) {
          imageLink.focus({ preventScroll: true });
        }
      }

      _destroySwipers() {
        if (this._mainSwiper && this._mainSwiper.destroy) {
          this._mainSwiper.destroy(true, true);
          this._mainSwiper = null;
        }
        if (this._thumbSwiper && this._thumbSwiper.destroy) {
          this._thumbSwiper.destroy(true, true);
          this._thumbSwiper = null;
        }
      }

      initSwipers() {
        if (typeof Swiper === 'undefined') return;
        this._destroySwipers();
        var mobile = isMobile();
        var verticalThumbs = usesVerticalThumbs();
        this._lastIsMobile = mobile;
        this._lastVerticalThumbs = verticalThumbs;

        if (this.thumbEl) {
          var syncThumbSize = this._syncThumbSlidesPerView.bind(this);
          var primeSlideSizes = this._primeSlideSizes.bind(this);
          var thumbOptions = {
            direction: verticalThumbs ? 'vertical' : 'horizontal',
            // CSS reserves a square for every thumbnail before its image loads.
            // A numeric count avoids Swiper 11's full update on every image load
            // in auto mode, and its per-slide computed-size reads on each update.
            slidesPerView: 1,
            spaceBetween: 8,
            on: {
              beforeInit: function (swiper) {
                syncThumbSize(swiper);
                primeSlideSizes(swiper);
              },
              beforeResize: syncThumbSize
            },
            // Preserve the thumbnail's action semantics after Swiper initializes.
            a11y: {
              slideRole: 'button',
              slideLabelMessage: 'View media {{index}}'
            },
            // Swiper 11 requires the object form. `freeMode: true` is silently
            // ignored, leaving the strip in snap mode where a touch drag snaps
            // back and feels stuck. The object form enables real free swiping
            // for touchscreens (touch laptops/monitors), pointer and mouse.
            freeMode: {
              enabled: true,
              sticky: false,
              momentum: true,
              // Lower resistance = less finger travel needed to move the strip.
              // Default velocityRatio is 5; reducing to 3 makes the strip feel
              // more responsive to short flicks on PC touchscreens.
              momentumRatio: 1,
              momentumBounceRatio: 0.3
            },
            watchSlidesProgress: true,
            // `watchOverflow: true` (Swiper default) locks the strip and disables
            // touch/mouse drag whenever all thumbnails fit inside the container
            // without overflowing. On wide PC screens with only a few images
            // (3-6 thumbs), this is almost always the case, so touch laptops
            // feel "stuck" even though allowTouchMove/simulateTouch are true.
            // Setting it to false keeps the strip draggable regardless of
            // content width, which is the expected UX for thumbnail strips.
            watchOverflow: verticalThumbs,
            simulateTouch: true,
            allowTouchMove: true,
            grabCursor: true,
            touchMoveStopPropagation: verticalThumbs,
            slideToClickedSlide: false,
            // Desktop vertical gestures stay inside the rail; mobile keeps the
            // existing edge-release behaviour for its horizontal strip.
            touchReleaseOnEdges: !verticalThumbs
          };
          this._thumbSwiper = new Swiper(this.thumbEl, thumbOptions);
        }

        if (this.mainEl) {
          var options = {
            on: {
              beforeInit: this._primeSlideSizes.bind(this)
            },
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            watchOverflow: true,
            allowTouchMove: mobile,
            simulateTouch: mobile,
            // A focused native video must still allow gallery swipes on iOS.
            // Keep pointer starts available to its play/pause and seek controls.
            focusableElements: 'input, select, option, textarea, button, label',
            touchStartPreventDefault: false,
            navigation: {
              prevEl: this.mainEl.querySelector('.swiper-button-prev'),
              nextEl: this.mainEl.querySelector('.swiper-button-next')
            },
            pagination: {
              el: this.mainEl.querySelector('.mg__pagination'),
              type: 'fraction'
            }
          };
          if (this._thumbSwiper) {
            options.thumbs = { swiper: this._thumbSwiper };
          }
          this._mainSwiper = new Swiper(this.mainEl, options);
          var self = this;
          this._mainSwiper.on('slideChange', function () {
            self._prepareNearbyMedia(self._mainSwiper.activeIndex);
          });
          this._prepareNearbyMedia(this._mainSwiper.activeIndex || 0);
        }

        this._updateSingleState();
      }

      _primeSlideSizes(swiper) {
        // 初次初始化也先批量写入数值尺寸，避免 Swiper 逐张读样式/写尺寸。
        // 保留 Swiper 自身的测量流程，处理隐藏容器和后续响应式变化。
        swiper.updateSize();
        var count = swiper.params.slidesPerView;
        if (!(swiper.size > 0) || typeof count !== 'number' || !(count > 0)) return;
        var gap = Number(swiper.params.spaceBetween) || 0;
        var size = (swiper.size - (count - 1) * gap) / count;
        var axis = swiper.isHorizontal() ? 'width' : 'height';
        Array.prototype.forEach.call(swiper.slidesEl.children, function (slide) {
          if (slide.classList.contains('swiper-slide')) slide.style[axis] = size + 'px';
        });
      }

      _syncThumbSlidesPerView(swiper) {
        var thumb = this.thumbEl && this.thumbEl.querySelector('.mg__thumb');
        if (!thumb || !swiper.el) return;
        var horizontal = swiper.isHorizontal();
        // Read the cross-axis size: Swiper only writes the main-axis size, so
        // this still follows the CSS token/clamp after a viewport change.
        var thumbSize = parseFloat(getComputedStyle(thumb)[horizontal ? 'height' : 'width']);
        var railStyle = getComputedStyle(swiper.el);
        swiper.params.spaceBetween = parseFloat(railStyle[horizontal ? 'columnGap' : 'rowGap']) || 8;
        var railSize = horizontal
          ? swiper.el.clientWidth - parseFloat(railStyle.paddingLeft) - parseFloat(railStyle.paddingRight)
          : swiper.el.clientHeight - parseFloat(railStyle.paddingTop) - parseFloat(railStyle.paddingBottom);
        if (!(thumbSize > 0) || !(railSize > 0)) return;
        var gap = Number(swiper.params.spaceBetween) || 0;
        // Invert Swiper's numeric sizing formula without rounding away the
        // partially visible last thumbnail. Hidden/empty rails keep their count
        // until a color switch or ResizeObserver makes them measurable again.
        swiper.params.slidesPerView = (railSize + gap) / (thumbSize + gap);
      }

      _thumbsOverflow() {
        if (!this._thumbSwiper || this._thumbSwiper.destroyed) return false;
        return this._thumbSwiper.virtualSize > this._thumbSwiper.size + 1;
      }

      _updateSingleState() {
        var count = this._getThumbSlides().length;
        this.dataset.single = count <= 1 ? 'true' : 'false';
      }

      _getThumbSlides() {
        if (!this._thumbSlides) {
          this._thumbSlides = this.thumbEl
            ? Array.prototype.slice.call(this.thumbEl.querySelectorAll('.swiper-slide'))
            : [];
        }
        return this._thumbSlides;
      }

      _invalidateMediaCache() {
        this._thumbSlides = null;
      }

      _replaceSlides(wrapper, slides, swiper) {
        if (!wrapper) return;
        // Reuse Swiper's last numeric size while the new slides are detached.
        // Otherwise updateSlides writes a size, then reads the next slide's
        // display style, which can force style/layout per slide. Keep update()
        // to correct stale sizes after resize or single/multiple-media changes.
        var axis = swiper && swiper.isHorizontal() ? 'width' : 'height';
        var previousSlide = swiper && swiper.slides && swiper.slides[0];
        var previousSize = previousSlide && previousSlide.style[axis];
        var reuseSize = swiper && typeof swiper.params.slidesPerView === 'number' &&
          parseFloat(previousSize) > 0;
        var fragment = document.createDocumentFragment();
        slides.forEach(function (slide) {
          if (reuseSize) slide.style[axis] = previousSize;
          fragment.appendChild(slide);
        });
        wrapper.textContent = '';
        wrapper.appendChild(fragment);
      }

      _destroyVideos() {
        var self = this;
        this._nativeVideos.forEach(function (controller, video) {
          var id = video.parentElement && video.parentElement.dataset.mediaId;
          if (id) self._videoPreferences.set(id, { userPaused: !!controller.userPaused, muted: video.muted, volume: video.volume });
          controller.destroy();
        });
        this._nativeVideos.clear();
      }

      _syncNativeVideos(activeSlide) {
        var self = this;
        // 先暂停旧项，再激活新项；未访问的视频不创建观察器或测量容器。
        this._nativeVideos.forEach(function (controller, video) {
          if (!activeSlide || !activeSlide.contains(video)) controller.setActive(false);
        });
        var videos = activeSlide ? activeSlide.querySelectorAll('video[data-src]') : [];
        if (!videos.length) return;
        if (!this._videoModule) {
          if (!this._videoImport && this.dataset.videoController) {
            this._videoImport = import(/* @vite-ignore */ this.dataset.videoController).then(function (module) {
              self._videoModule = module;
              if (self.isConnected) self._prepareNearbyMedia(self._activeVideoIndex || 0);
            }).catch(function () { self.dataset.videoLoadError = 'true'; });
          }
          return;
        }
        videos.forEach(function (video) {
          var active = !!activeSlide && activeSlide.contains(video);
          var existing = self._nativeVideos.get(video);
          if (existing) { existing.setActive(active); return; }
          var box = video.parentElement;
          var saved = self._videoPreferences.get(box.dataset.mediaId);
          if (saved) { video.muted = saved.muted; video.volume = saved.volume; }
          var sources = self._videoModule.videoSources(video.dataset.video, video.dataset.src);
          var controller = new self._videoModule.ThemeVideoController(video, {
            container: box,
            sources: function () { return { key: video.dataset.src, sources: sources }; },
            active: active,
            autoplay: (video.dataset.autoplay || self.dataset.videoAutoplay) !== 'false',
            userPaused: saved && saved.userPaused,
            maxDpr: 2,
            minEdge: 720,
            poster: box.querySelector('img.mg__video-poster'),
            fallbackPoster: video.dataset.poster,
            onState: function (state) {
              box.toggleAttribute('data-video-frame-ready', state.frameReady);
              // 自动播放被拒时保留原生控件；仅实际媒体错误回退到独立封面。
              box.toggleAttribute('data-video-error', state.blocked && !!video.error);
            }
          });
          self._nativeVideos.set(video, controller);
        });
      }

      _loadDeferredMedia(media) {
        // YouTube 保留原路径；原生视频仅由共享控制器挂载源和播放。
        if (media && !media.getAttribute('src') && media.dataset.src) media.src = media.dataset.src;
      }

      _prepareNearbyMedia(activeIndex) {
        if (!this.mainEl || this._updatingGroup) return;
        this._activeVideoIndex = activeIndex;
        var slides = this.mainEl.querySelectorAll('.swiper-slide');
        var imageStart = Math.max(activeIndex - 1, 0);
        var imageEnd = Math.min(activeIndex + 1, slides.length - 1);

        for (var index = imageStart; index <= imageEnd; index += 1) {
          slides[index].querySelectorAll('img[loading="lazy"]').forEach(function (image) {
            image.loading = 'eager';
            image.fetchPriority = image.classList.contains('ds-responsive-image__placeholder') ? 'low' :
              (index === activeIndex ? 'high' : 'auto');
            // Keep distant slides out of the native lazy-load prefetch window.
            // Sizes and priority must be set before exposing either image URL.
            if (image.dataset.mgSrcset) {
              image.srcset = image.dataset.mgSrcset;
              delete image.dataset.mgSrcset;
            }
            if (image.dataset.mgSrc) {
              image.src = image.dataset.mgSrc;
              delete image.dataset.mgSrc;
            }
          });
        }

        var activeSlide = slides[activeIndex];
        this._syncNativeVideos(activeSlide);
        if (activeSlide) activeSlide.querySelectorAll('iframe[data-src]').forEach(this._loadDeferredMedia.bind(this));
      }

      /* Fix cached/lazy images that won't re-fire onload after DOM injection.
         Only the first (active) main slide is promoted to eager so the visible
         image loads immediately; remaining slides keep loading="lazy" to avoid
         a burst of parallel downloads after a variant switch. */
      _fixBlurPlaceholders() {
        var mainSlides = this.querySelectorAll('.mg__main .swiper-slide');
        var firstMainImg = null;
        if (mainSlides.length) {
          var firstSlide = mainSlides[0];
          firstMainImg = firstSlide.querySelector('img.ds-responsive-image__main') ||
            firstSlide.querySelector('img:not(.ds-responsive-image__placeholder)');
        }

        this.querySelectorAll('.ds-responsive-image:not(.is-loaded)').forEach(function (wrap) {
          var img = wrap.querySelector('img.ds-responsive-image__main') ||
            wrap.querySelector('img:not(.ds-responsive-image__placeholder)');
          if (!img) return;

          // Only promote the first visible main image to eager
          if (img === firstMainImg) {
            img.loading = 'eager';
          }

          if (img.complete && img.naturalWidth > 0) {
            wrap.classList.add('is-loaded');
          } else {
            img.addEventListener('load', function () {
              wrap.classList.add('is-loaded');
            }, { once: true });
          }
        });
      }

      /* Use the same width query as Liquid image_url so a color round trip
         can reuse the browser cache instead of downloading _800x aliases. */
      _resizedUrl(url, width) {
        if (!url) return url;
        var hashIndex = url.indexOf('#');
        var hash = hashIndex > -1 ? url.slice(hashIndex) : '';
        var source = hashIndex > -1 ? url.slice(0, hashIndex) : url;
        var qIndex = source.indexOf('?');
        var base = qIndex > -1 ? source.slice(0, qIndex) : source;
        var query = new URLSearchParams(qIndex > -1 ? source.slice(qIndex + 1) : '');
        query.set('width', width);
        return base + '?' + query.toString() + hash;
      }

      _srcsetFor(url, nativeWidth, widths) {
        var self = this;
        var maxWidth = Math.min(nativeWidth, Math.max.apply(null, widths));
        return Array.from(new Set(widths.concat(maxWidth)))
          .filter(function (w) { return w > 0 && w <= maxWidth; })
          .sort(function (a, b) { return a - b; })
          .map(function (w) { return self._resizedUrl(url, w) + ' ' + w + 'w'; })
          .join(', ');
      }

      /* Build main + thumb slides for one color group from the compact
         metadata payload (see media-gallery.liquid). Reuse the rendered
         slide/anchor attributes and zoom icon, but create fresh images.
         Cloning old images starts redundant loads and copies onload handlers
         that can run after their wrappers have been removed. Must run
         BEFORE the current slides are removed. */
      _buildCompactGroup(compact) {
        var sectionId = this.sectionId;
        var self = this;
        // Liquid owns the policy; the fallback covers cached/older markup.
        var mainWidths = (this.dataset.mainWidths || '360,480,600,720,800,960,1200,1440,1600').split(',').map(Number);
        var thumbWidths = (this.dataset.thumbWidths || '64,96,128,160,192,240').split(',').map(Number);
        var previewWidths = (this.dataset.previewWidths || '480,600,720,800,960,1200,1440,1600,2000,2400,3200,4800,5760').split(',').map(Number);

        var mainAnchor = this.mainEl && this.mainEl.querySelector('.swiper-slide a.media-gallery__image');
        var imageTemplate = document.querySelector('template[data-mg-image="' + sectionId + '"]');
        var mainTplSlide = mainAnchor ? mainAnchor.closest('.swiper-slide') :
          (imageTemplate ? imageTemplate.content.firstElementChild : null);
        var mainTplAnchor = mainTplSlide && mainTplSlide.querySelector('a.media-gallery__image');
        var mainTplImg = mainTplAnchor && (mainTplAnchor.querySelector('img.ds-responsive-image__main') ||
          mainTplAnchor.querySelector('img:not(.ds-responsive-image__placeholder)'));
        var mainSizes = (compact.length <= 1 ? this.dataset.singleImageSizes : this.dataset.mainSizes) ||
          (mainTplImg ? mainTplImg.getAttribute('sizes') : '100vw');
        var mainTplZoom = mainTplAnchor && mainTplAnchor.querySelector('.mg__zoom-icon');
        var badgeTemplate = document.querySelector('template[data-mg-video-badge="' + sectionId + '"]');
        var badgeEl = badgeTemplate && badgeTemplate.content.firstElementChild;

        var mainEls = [];
        var thumbEls = [];
        var imageIndex = 0;

        compact.forEach(function (m) {
          var mediaId = sectionId + '-' + m.mid;
          var isVideo = m.type === 'video';
          if (!isVideo) imageIndex += 1;

          /* ---- Main slide ---- */
          var slide;
          if (isVideo) {
            slide = document.createElement('div');
            slide.className = 'swiper-slide';
            slide.innerHTML =
              '<div class="mg__media mg__media--video" data-media-id="' + mediaId + '">' +
                '<video playsinline muted controls preload="none" data-src="" data-poster=""></video>' +
              '</div>';
            var mediaBox = slide.firstChild;
            mediaBox.style.setProperty('--aspect-ratio', m.ratio);
            var video = mediaBox.querySelector('video');
            video.dataset.autoplay = self.dataset.videoAutoplay !== 'false' ? 'true' : 'false';
            video.setAttribute('aria-label', m.alt || 'Product video');
            video.loop = self.dataset.videoLoop !== 'false';
            video.dataset.src = m.src || '';
            if (m.video) video.dataset.video = JSON.stringify(m.video);
            video.dataset.poster = self._resizedUrl(m.pu, 800);
            if (m.pu) {
              var poster = document.createElement('img');
              poster.className = 'mg__video-poster';
              poster.alt = m.alt || '';
              poster.width = m.pw;
              poster.height = m.ph;
              poster.loading = 'lazy';
              poster.sizes = mainSizes;
              poster.dataset.mgSrcset = self._srcsetFor(m.pu, m.pw, [360, 480, 600, 720, 800]);
              poster.dataset.mgSrc = self._resizedUrl(m.pu, Math.min(m.pw, 800));
              mediaBox.insertBefore(poster, video);
            }
          } else if (mainTplAnchor) {
            slide = mainTplSlide.cloneNode(false);
            var anchor = mainTplAnchor.cloneNode(false);
            slide.appendChild(anchor);
            if (mainTplZoom) anchor.appendChild(mainTplZoom.cloneNode(true));
            var previewWidth = Math.min(m.pw, Math.max.apply(null, previewWidths));
            if (self.dataset.zoom === 'open_lightbox') {
              anchor.href = self._resizedUrl(m.pu, previewWidth);
            } else {
              anchor.removeAttribute('href');
            }
            anchor.setAttribute('data-pswp-srcset', self._srcsetFor(m.pu, m.pw, previewWidths));
            anchor.setAttribute('data-media-id', mediaId);
            anchor.setAttribute('data-pswp-width', previewWidth);
            anchor.setAttribute('data-pswp-height', Math.floor(previewWidth * m.ph / m.pw));
            anchor.setAttribute('data-position', imageIndex);
            anchor.setAttribute('data-extra-position', imageIndex);
            anchor.style.setProperty('--aspect-ratio', m.ratio);
            // Rebuild the image area from scratch, same reason as thumbs:
            // cloned <img> nodes carry the previous color's bitmap and keep
            // showing it (a visible flash) until the new download finishes.
            // A fresh placeholder+main pair shows the 20px blur instantly and
            // fades in sharp once the real image loads.
            var wrap = document.createElement('div');
            wrap.className = 'ds-responsive-image';
            var phImg = document.createElement('img');
            phImg.className = 'ds-responsive-image__placeholder';
            phImg.loading = 'lazy';
            phImg.fetchPriority = 'low';
            phImg.dataset.mgSrc = self._resizedUrl(m.pu, 20);
            phImg.setAttribute('width', 20);
            phImg.setAttribute('height', Math.round(20 * m.ph / m.pw) || 20);
            phImg.alt = '';
            phImg.setAttribute('aria-hidden', 'true');
            var newMainImg = document.createElement('img');
            newMainImg.className = 'ds-responsive-image__main';
            newMainImg.loading = 'lazy';
            newMainImg.fetchPriority = 'low';
            if (mainSizes) newMainImg.setAttribute('sizes', mainSizes);
            newMainImg.dataset.mgSrcset = self._srcsetFor(m.pu, m.pw, mainWidths);
            newMainImg.dataset.mgSrc = self._resizedUrl(m.pu, Math.min(m.pw, 800));
            newMainImg.setAttribute('width', m.pw);
            newMainImg.setAttribute('height', m.ph);
            newMainImg.alt = m.alt || '';
            wrap.appendChild(phImg);
            wrap.appendChild(newMainImg);
            var zoomIcon = anchor.querySelector('.mg__zoom-icon');
            if (zoomIcon) {
              anchor.insertBefore(wrap, zoomIcon);
            } else {
              anchor.appendChild(wrap);
            }
          } else {
            slide = document.createElement('div');
            slide.className = 'swiper-slide';
          }
          mainEls.push(slide);

          /* ---- Thumb slide ---- */
          // Built from scratch on purpose: cloning the live thumb carried the
          // previous color's bitmap/src into the new strip, so every thumb
          // flashed the old color's image until its own download finished.
          // A fresh <img> shows the neutral skeleton background (CSS) until
          // its own source arrives.
          var thumb = document.createElement('div');
          thumb.className = 'mg__thumb swiper-slide';
          thumb.setAttribute('role', 'button');
          thumb.setAttribute('tabindex', '0');
          thumb.setAttribute('data-media-id', mediaId);
          thumb.setAttribute('data-media-index', m.index);
          thumb.setAttribute('aria-label', 'View media ' + m.index);
          var thumbImg = document.createElement('img');
          thumbImg.alt = m.alt || '';
          thumbImg.loading = 'lazy';
          thumbImg.setAttribute('sizes', self.dataset.thumbSizes || '(min-width: 1366px) 75px, (min-width: 1024px) 50px, 64px');
          thumbImg.setAttribute('width', m.pw);
          thumbImg.setAttribute('height', m.ph);
          thumbImg.setAttribute('srcset', self._srcsetFor(m.pu, m.pw, thumbWidths));
          thumbImg.src = self._resizedUrl(m.pu, Math.min(m.pw, 160));
          thumb.appendChild(thumbImg);
          // Keep the play badge only on video thumbs.
          if (isVideo && badgeEl) {
            thumb.appendChild(badgeEl.cloneNode(true));
          }
          thumbEls.push(thumb);
        });

        return { main: mainEls, thumb: thumbEls };
      }

      _parseSlideHtml(html) {
        var holder = document.createElement('div');
        holder.innerHTML = html || '';
        return Array.prototype.slice.call(holder.children);
      }

      switchGroup(key) {
        var templates = this._getTemplates();
        if (!templates || !templates[key]) return;
        if (key === this._currentGroupKey) {
          this.classList.remove('is-switching');
          return;
        }
        var tpl = templates[key];
        var mainWrapper = this.mainEl && this.mainEl.querySelector('.swiper-wrapper');
        var thumbWrapper = this.thumbEl && this.thumbEl.querySelector('.swiper-wrapper');
        if (!mainWrapper || !thumbWrapper) return;

        // Build before removing the current slides; fallback templates preserve
        // Shopify video markup and never reuse the previous color's media.
        var built;
        if (tpl.useProductMedia) {
          var fallbackSelector = 'template[data-mg-product-media="' + this.sectionId + '"]';
          var mainTemplate = document.querySelector(fallbackSelector + '[data-thumbnail="false"]');
          var thumbTemplate = document.querySelector(fallbackSelector + '[data-thumbnail="true"]');
          if (!mainTemplate || !thumbTemplate) return;
          built = {
            main: Array.from(mainTemplate.content.children, function (node) { return node.cloneNode(true); }),
            thumb: Array.from(thumbTemplate.content.children, function (node) { return node.cloneNode(true); })
          };
        } else {
          built = tpl.compact
            ? this._buildCompactGroup(tpl.compact)
            : { main: this._parseSlideHtml(tpl.main), thumb: this._parseSlideHtml(tpl.thumb) };
        }
        var wasSingle = this.dataset.single === 'true';
        this.classList.add('is-switching');
        this._destroyVideos();

        this._currentGroupKey = key;
        this._invalidateMediaCache();

        // Reuse the live Swiper instances: swapping slides in place skips the
        // destroy/re-init cost (and duplicate listener setup) that a full
        // rebuild incurred on every color switch (mobile INP hot path).
        var reused = this._mainSwiper && this._thumbSwiper && !this._mainSwiper.destroyed;
        // update() can clamp the old index in a shorter group and emit slideChange.
        // Do not hydrate that temporary position; load only after the final reset.
        this._updatingGroup = true;
        try {
          if (reused) {
            // Swiper's removeAllSlides/appendSlide each trigger their own update.
            // Replace both wrappers in one DOM write, then update each instance once.
            this._replaceSlides(thumbWrapper, built.thumb, this._thumbSwiper);
            this._replaceSlides(mainWrapper, built.main, this._mainSwiper);
            this._updateSingleState();
            // 水平轨道宽度不随图片组变化；resize 仍会重新测量。
            // 垂直轨道高度和单图/多图布局可能改变，继续使用实时尺寸。
            if (!this._thumbSwiper.isHorizontal() || wasSingle !== (this.dataset.single === 'true')) {
              this._syncThumbSlidesPerView(this._thumbSwiper);
            }
            this._thumbSwiper.update();
            this._mainSwiper.update();
            this._thumbSwiper.slideTo(0, 0);
            this._mainSwiper.slideTo(0, 0);
          } else {
            this._destroySwipers();
            mainWrapper.innerHTML = '';
            thumbWrapper.innerHTML = '';
            built.main.forEach(function (el) { mainWrapper.appendChild(el); });
            built.thumb.forEach(function (el) { thumbWrapper.appendChild(el); });
            this.initSwipers();
          }
        } finally {
          this._updatingGroup = false;
        }
        this._prepareNearbyMedia(0);

        var self = this;
        requestAnimationFrame(function () {
          self._fixBlurPlaceholders();
          self.classList.remove('is-switching');
        });
      }

      /* ---- Variant integration ---- */

      _getProductRoot() {
        return this.closest('.product__item-js') || document;
      }

      _getVariantPicker() {
        var root = this._getProductRoot();
        return root.querySelector('variant-radios-detail');
      }

      bindVariantSwitcher(syncSelection) {
        var picker = this._getVariantPicker();
        var self = this;

        if (picker) {
          if (this._variantPicker !== picker) {
            picker.addEventListener('change', function (event) {
              // Color controls live outside the gallery. Their first change must
              // wake it too; init() will read the latest selection after yielding.
              if (!self._ready) {
                self._handleEarlyInteraction(event);
                return;
              }
              var value = event.target && event.target.value;
              var templates = value ? self._getTemplates() : null;
              if (value && templates && templates[value]) {
                // Fade out now, debounce the heavy rebuild: a burst of quick
                // swatch clicks collapses into one render of the final variant.
                self.classList.add('is-switching');
                self._debouncedSwitchGroup(value);
              } else {
                requestAnimationFrame(function () {
                  self._syncFeaturedMedia(picker);
                });
              }
            }, { signal: this._connectionAbort.signal });
            this._variantPicker = picker;
          }
          if (syncSelection === false) return;
          // Color-group galleries already start with the correct first media,
          // including the product-media fallback. Do not jump to featured_media.
          var colorFieldset = picker.querySelector('.product-form__input_color');
          var colorValueCount = colorFieldset
            ? colorFieldset.querySelectorAll('input[type="radio"], select option').length
            : 0;
          var templates = this._getTemplates();
          var selectedColor = colorFieldset && colorFieldset.querySelector('input[type="radio"]:checked, select');
          // Read the latest color when deferred or interaction-driven init completes.
          if (templates && selectedColor && templates[selectedColor.value]) {
            this.switchGroup(selectedColor.value);
          } else if (!templates && this.dataset.variantImages !== 'true' && colorValueCount !== 1) {
            this._syncFeaturedMedia(picker);
          }
          return;
        }

      }

      _getCurrentVariant(picker) {
        if (!picker) return null;
        var json = picker.querySelector('script[type="application/json"]');
        if (!json) return null;
        var variants;
        try {
          variants = JSON.parse(json.textContent || '[]');
        } catch (e) {
          return null;
        }
        var selected = Array.from(picker.querySelectorAll('fieldset')).map(function (fs) {
          var checked = fs.querySelector('input[type="radio"]:checked');
          if (checked) return checked.value;
          var select = fs.querySelector('select');
          return select ? select.value : null;
        });
        if (!selected.length || selected.some(function (v) { return v == null; })) return null;
        return variants.find(function (variant) {
          return selected.every(function (value, i) {
            return variant['option' + (i + 1)] === value;
          });
        }) || null;
      }

      _syncFeaturedMedia(picker) {
        var variant = this._getCurrentVariant(picker);
        if (!variant || !variant.featured_media) return;
        var mediaId = this.sectionId + '-' + variant.featured_media.id;
        var thumbs = this._getThumbSlides();
        var target = thumbs.find(function (t) { return t.dataset.mediaId === mediaId; });
        if (!target) return;
        var index = thumbs.indexOf(target);
        if (this._thumbSwiper) this._thumbSwiper.slideTo(index, 0);
        if (this._mainSwiper && index >= 0) this._mainSwiper.slideTo(index, 0);
      }

      /* ---- Thumbnail peek (soft loop) ---- */
      /* On thumbnail tap, nudge the strip forward one slide to hint that more
         images follow; after the last thumb, wrap back to the first (modulo).
         Bound on the persistent thumb container via delegation so it survives
         variant rebuilds that only replace the inner .swiper-wrapper markup. */
      _bindThumbPeek() {
        if (!this.thumbEl || this.thumbEl.dataset.mgPeekBound === 'true') return;
        var self = this;
        this.thumbEl.addEventListener('click', function (event) {
          var slide = event.target && event.target.closest
            ? event.target.closest('.swiper-slide')
            : null;
          if (!slide || !self.thumbEl.contains(slide)) return;
          var slides = self._getThumbSlides();
          var idx = slides.indexOf(slide);
          if (idx < 0) return;
          var total = slides.length;
          if (total <= 1) return;
          // Let Swiper finish its own click→main-slide handling first, then
          // align the clicked thumb to the start of the visible strip.
          // No modulo wrap: the last thumb simply stays at the strip's end.
          requestAnimationFrame(function () {
            if (self._thumbSwiper) self._thumbSwiper.slideTo(idx);
          });
        });
        // Thumbnails were switched from <button> to <div role="button"> so
        // touch laptops can swipe the strip without the browser's button
        // touch-defaults fighting Swiper. Keyboard activation must select both
        // sliders directly: a synthetic click does not emit Swiper's pointer tap.
        this.thumbEl.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          var slide = event.target && event.target.closest
            ? event.target.closest('.mg__thumb')
            : null;
          if (!slide || !self.thumbEl.contains(slide)) return;
          event.preventDefault();
          var idx = self._getThumbSlides().indexOf(slide);
          if (idx < 0) return;
          if (self._mainSwiper) self._mainSwiper.slideTo(idx);
          if (self._thumbSwiper) self._thumbSwiper.slideTo(idx);
        });
        // Keep desktop wheel gestures inside the vertical thumbnail rail so
        // the adjacent product details do not move while media is browsed.
        // Mobile retains the horizontal strip's existing edge-release model.
        this.thumbEl.addEventListener('wheel', function (event) {
          if (!self._thumbSwiper || !self._thumbsOverflow()) return;
          var vertical = usesVerticalThumbs();
          var delta = vertical
            ? event.deltaY
            : (Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY);
          if (vertical) event.preventDefault();
          // Threshold to avoid accidental micro-scrolls (trackpad noise).
          if (Math.abs(delta) < 10) return;
          var direction = delta > 0 ? 1 : -1;
          if ((direction < 0 && self._thumbSwiper.isBeginning) ||
              (direction > 0 && self._thumbSwiper.isEnd)) return;

          if (!vertical) event.preventDefault();
          // Move one slide per wheel gesture. Short debounce (80ms) keeps the
          // strip feeling responsive during continuous two-finger swipes while
          // still preventing a single gesture from firing multiple slideTo calls.
          if (!self._thumbWheelLock) {
            self._thumbWheelLock = true;
            var nextIndex = Math.min(
              Math.max(self._thumbSwiper.activeIndex + direction, 0),
              self._thumbSwiper.slides.length - 1
            );
            self._thumbSwiper.slideTo(nextIndex, 150);
            setTimeout(function () {
              self._thumbWheelLock = false;
            }, 80);
          }
        }, { passive: false });
        this.thumbEl.dataset.mgPeekBound = 'true';
      }

      _onResize() {
        // Resizing must not bypass the gallery's deferred startup.
        if (!this._ready) return;
        var mobile = isMobile();
        var verticalThumbs = usesVerticalThumbs();
        var galleryWidth = this.getBoundingClientRect().width;
        if (mobile !== this._lastIsMobile || verticalThumbs !== this._lastVerticalThumbs) {
          var activeIndex = this._mainSwiper ? this._mainSwiper.activeIndex : 0;
          this._lastGalleryWidth = galleryWidth;
          this.initSwipers();
          if (this._mainSwiper) this._mainSwiper.slideTo(activeIndex, 0);
        } else if (Math.abs(galleryWidth - this._lastGalleryWidth) > 1) {
          this._lastGalleryWidth = galleryWidth;
          if (this._mainSwiper) this._mainSwiper.update();
          if (this._thumbSwiper) {
            this._syncThumbSlidesPerView(this._thumbSwiper);
            this._thumbSwiper.update();
          }
        }
      }
    };

    if (!customElements.get('media-gallery')) {
      customElements.define('media-gallery', MediaGallery);
    }
  }
})();
